import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, Key, Flag, DollarSign, Users, 
  Plus, Trash2, Copy, Download, RefreshCw,
  ChevronRight, AlertTriangle, Gift, MessageSquare,
  CheckCircle, XCircle, Clock, ExternalLink, User,
  Menu, X, Image, Palette, ScrollText, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * Founder Console — Utilitarian Admin Tool
 * 
 * Modules:
 * - Courtesy Codes: 6-month courtesy access codes (P1)
 * - Feedback: User feedback tracking (P1)
 * - Codes: Beta invite codes, unlock codes
 * - Moderation: Flags, reports, actions
 * - Financials: Platform summary, payouts
 * - Users: Lookup, account status
 * 
 * Design: Dark, utilitarian, tables over cards
 * Mobile: Drawer sidebar with backdrop
 */

export default function FounderConsole() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('courtesy');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-close sidebar on navigation (key for mobile UX)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Check founder access
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Check if user has founder/admin role
    if (user.role !== 'founder' && user.role !== 'admin' && !user.is_admin) {
      setError('Founder access required');
      return;
    }
    
    fetchDashboard();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Try to fetch dashboard, but don't fail if it doesn't exist
      try {
        const res = await axios.get(`${API}/api/founder/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboard(res.data);
      } catch (dashErr) {
        // Dashboard endpoint might not exist, that's okay
        console.log('Dashboard endpoint not available, using defaults');
        setDashboard({ users: { total: 0, members: 0 }, moderation: { pending_flags: 0 }, codes: { active: 0 } });
      }
      setError(null);
    } catch (err) {
      console.error('Dashboard error:', err);
      if (err.response?.status === 403) {
        setError('Founder access required');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change - close sidebar on mobile
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{error}</h1>
          <p className="text-zinc-400 mb-4">You don&apos;t have permission to access this page.</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-zinc-600 text-zinc-300">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-zinc-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 text-zinc-100">
      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside className={`
        fixed top-0 left-0 h-full w-[80%] max-w-[300px] bg-zinc-800 z-50 
        transform transition-transform duration-300 ease-in-out
        md:hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            <span className="font-bold text-white">Console</span>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="p-2 text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="p-2">
          <SidebarNavButton 
            active={activeTab === 'courtesy'} 
            onClick={() => handleTabChange('courtesy')}
            icon={<Gift className="w-5 h-5" />}
            label="Courtesy Codes"
          />
          <SidebarNavButton 
            active={activeTab === 'feedback'} 
            onClick={() => handleTabChange('feedback')}
            icon={<MessageSquare className="w-5 h-5" />}
            label="Feedback"
          />
          <SidebarNavButton 
            active={activeTab === 'codes'} 
            onClick={() => handleTabChange('codes')}
            icon={<Key className="w-5 h-5" />}
            label="Beta Codes"
          />
          <SidebarNavButton 
            active={activeTab === 'moderation'} 
            onClick={() => handleTabChange('moderation')}
            icon={<Flag className="w-5 h-5" />}
            label="Moderation"
            badge={dashboard?.moderation?.pending_flags}
          />
          <SidebarNavButton 
            active={activeTab === 'financials'} 
            onClick={() => handleTabChange('financials')}
            icon={<DollarSign className="w-5 h-5" />}
            label="Financials"
          />
          <SidebarNavButton 
            active={activeTab === 'users'} 
            onClick={() => handleTabChange('users')}
            icon={<Users className="w-5 h-5" />}
            label="Users"
          />
          <SidebarNavButton 
            active={activeTab === 'appearance'} 
            onClick={() => handleTabChange('appearance')}
            icon={<Shield className="w-5 h-5" />}
            label="Appearance"
          />
          <SidebarNavButton 
            active={activeTab === 'constitution'} 
            onClick={() => handleTabChange('constitution')}
            icon={<ScrollText className="w-5 h-5" />}
            label="Constitution"
          />
          <SidebarNavButton 
            active={activeTab === 'verification'} 
            onClick={() => handleTabChange('verification')}
            icon={<Building2 className="w-5 h-5" />}
            label="Verification"
          />
        </nav>
      </aside>

      {/* Header */}
      <header className="bg-zinc-800 border-b border-zinc-700 px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-zinc-400 hover:text-white md:hidden"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Shield className="w-6 h-6 text-amber-500 hidden md:block" />
            <h1 className="text-base md:text-lg font-bold text-white">
              <span className="hidden sm:inline">LIKEKIND </span>FOUNDER CONSOLE
            </h1>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/founder/policy-log')}
              className="text-red-400 hover:text-red-300 hidden sm:flex"
            >
              <Flag className="w-4 h-4 mr-1" />
              Policy Log
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/founder/sponsors')}
              className="text-amber-400 hover:text-amber-300 hidden sm:flex"
            >
              <DollarSign className="w-4 h-4 mr-1" />
              Sponsors
            </Button>
            <span className="text-xs md:text-sm text-zinc-400 hidden sm:block">{user?.email}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-white"
            >
              Exit
            </Button>
          </div>
        </div>
      </header>

      {/* Dashboard Summary */}
      {dashboard && (
        <div className="bg-zinc-800/50 border-b border-zinc-700 px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <StatCard label="Total Users" value={dashboard.users?.total || 0} />
            <StatCard label="Members" value={dashboard.users?.members || 0} />
            <StatCard label="Pending Flags" value={dashboard.moderation?.pending_flags || 0} color="amber" />
            <StatCard label="Active Codes" value={dashboard.codes?.active || 0} color="green" />
          </div>
        </div>
      )}

      {/* Tab Navigation - Desktop only (horizontal tabs) */}
      <div className="border-b border-zinc-700 hidden md:block">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-1 overflow-x-auto">
            <TabButton 
              active={activeTab === 'courtesy'} 
              onClick={() => setActiveTab('courtesy')}
              icon={<Gift className="w-4 h-4" />}
              label="Courtesy Codes"
            />
            <TabButton 
              active={activeTab === 'feedback'} 
              onClick={() => setActiveTab('feedback')}
              icon={<MessageSquare className="w-4 h-4" />}
              label="Feedback"
            />
            <TabButton 
              active={activeTab === 'codes'} 
              onClick={() => setActiveTab('codes')}
              icon={<Key className="w-4 h-4" />}
              label="Beta Codes"
            />
            <TabButton 
              active={activeTab === 'moderation'} 
              onClick={() => setActiveTab('moderation')}
              icon={<Flag className="w-4 h-4" />}
              label="Moderation"
              badge={dashboard?.moderation?.pending_flags}
            />
            <TabButton 
              active={activeTab === 'financials'} 
              onClick={() => setActiveTab('financials')}
              icon={<DollarSign className="w-4 h-4" />}
              label="Financials"
            />
            <TabButton 
              active={activeTab === 'users'} 
              onClick={() => setActiveTab('users')}
              icon={<Users className="w-4 h-4" />}
              label="Users"
            />
            <TabButton 
              active={activeTab === 'appearance'} 
              onClick={() => setActiveTab('appearance')}
              icon={<Shield className="w-4 h-4" />}
              label="Appearance"
            />
            <TabButton 
              active={activeTab === 'constitution'} 
              onClick={() => setActiveTab('constitution')}
              icon={<ScrollText className="w-4 h-4" />}
              label="Constitution"
            />
            <TabButton 
              active={activeTab === 'verification'} 
              onClick={() => setActiveTab('verification')}
              icon={<Building2 className="w-4 h-4" />}
              label="Verification"
            />
          </nav>
        </div>
      </div>

      {/* Mobile Tab Indicator */}
      <div className="bg-zinc-800/30 px-4 py-2 border-b border-zinc-700 md:hidden">
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <span>Current:</span>
          <span className="text-amber-500 font-medium capitalize">{activeTab.replace('-', ' ')}</span>
        </div>
      </div>

      {/* Tab Content - no left margin on mobile */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
        {activeTab === 'courtesy' && <CourtesyCodesModule />}
        {activeTab === 'feedback' && <FeedbackModule />}
        {activeTab === 'codes' && <CodesModule />}
        {activeTab === 'moderation' && <ModerationModule />}
        {activeTab === 'financials' && <FinancialsModule dashboard={dashboard} />}
        {activeTab === 'users' && <UsersModule />}
        {activeTab === 'appearance' && <AppearanceModule />}
        {activeTab === 'constitution' && <ConstitutionModule />}
        {activeTab === 'verification' && <BusinessVerificationModule />}
      </main>
    </div>
  );
}

// ============ STAT CARD ============

function StatCard({ label, value, color = 'zinc' }) {
  const colorClasses = {
    zinc: 'text-zinc-100',
    amber: 'text-amber-400',
    green: 'text-green-400',
    red: 'text-red-400'
  };
  
  return (
    <div className="bg-zinc-800 rounded-lg p-4">
      <div className={`text-2xl font-bold ${colorClasses[color]}`}>{value}</div>
      <div className="text-sm text-zinc-400">{label}</div>
    </div>
  );
}

// ============ TAB BUTTON ============

function TabButton({ active, onClick, icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
        active 
          ? 'border-amber-500 text-white' 
          : 'border-transparent text-zinc-400 hover:text-zinc-200'
      }`}
    >
      {icon}
      {label}
      {badge > 0 && (
        <span className="bg-amber-500 text-zinc-900 text-xs px-1.5 py-0.5 rounded-full font-bold">
          {badge}
        </span>
      )}
    </button>
  );
}

// ============ SIDEBAR NAV BUTTON (Mobile) ============

function SidebarNavButton({ active, onClick, icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
        active 
          ? 'bg-amber-500/20 text-amber-400' 
          : 'text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200'
      }`}
    >
      {icon}
      <span className="flex-1 font-medium">{label}</span>
      {badge > 0 && (
        <span className="bg-amber-500 text-zinc-900 text-xs px-2 py-0.5 rounded-full font-bold">
          {badge}
        </span>
      )}
    </button>
  );
}

// ============ COURTESY CODES MODULE ============

function CourtesyCodesModule() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/courtesy/codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(res.data.codes || []);
    } catch (err) {
      console.error('Failed to load courtesy codes:', err);
      toast.error('Failed to load courtesy codes');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCodes = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/courtesy/generate?count=10`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('10 courtesy codes generated');
      fetchCodes();
    } catch (err) {
      console.error('Failed to generate codes:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate codes');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (codeId) => {
    if (!window.confirm('Revoke this code? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/courtesy/revoke/${codeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code revoked');
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      unused: 'bg-green-900/50 text-green-400',
      redeemed: 'bg-blue-900/50 text-blue-400',
      revoked: 'bg-red-900/50 text-red-400'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded ${styles[status] || 'bg-zinc-700 text-zinc-400'}`}>
        {status}
      </span>
    );
  };

  // Calculate stats
  const unusedCount = codes.filter(c => c.status === 'unused').length;
  const redeemedCount = codes.filter(c => c.status === 'redeemed').length;
  const revokedCount = codes.filter(c => c.status === 'revoked').length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Courtesy Access Codes</h2>
          <p className="text-sm text-zinc-400 mt-1">
            One-time-use codes granting 6 months of free access
          </p>
        </div>
        {codes.length === 0 && (
          <Button 
            onClick={handleGenerateCodes} 
            disabled={generating}
            className="bg-amber-500 hover:bg-amber-600 text-zinc-900"
          >
            {generating ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" /> Generate 10 Codes</>
            )}
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-2xl font-bold text-green-400">{unusedCount}</span>
          </div>
          <div className="text-sm text-zinc-400">Available</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-blue-400" />
            <span className="text-2xl font-bold text-blue-400">{redeemedCount}</span>
          </div>
          <div className="text-sm text-zinc-400">Redeemed</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-400" />
            <span className="text-2xl font-bold text-red-400">{revokedCount}</span>
          </div>
          <div className="text-sm text-zinc-400">Revoked</div>
        </div>
      </div>

      {/* Codes Table */}
      <div className="bg-zinc-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
          <h3 className="font-bold">All Codes ({codes.length})</h3>
          <Button variant="outline" size="sm" onClick={fetchCodes} className="border-zinc-600">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-700">
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Code</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Redeemed By</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Expires At</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Created</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-zinc-400">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading...
                </td>
              </tr>
            ) : codes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-zinc-400">
                  <Gift className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No courtesy codes yet</p>
                  <p className="text-sm mt-1">Click &quot;Generate 10 Codes&quot; to create your first batch</p>
                </td>
              </tr>
            ) : codes.map((code) => (
              <tr key={code.code_id} className="border-b border-zinc-700/50 hover:bg-zinc-700/30">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono text-amber-400">{code.code_value}</code>
                    <button onClick={() => copyCode(code.code_value)} className="text-zinc-500 hover:text-zinc-300">
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(code.status)}
                </td>
                <td className="px-4 py-3">
                  {code.redeemed_by_user ? (
                    <Link 
                      to={`/profile/${code.redeemed_by_user_id}`}
                      className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
                    >
                      <User className="w-3 h-3" />
                      {code.redeemed_by_user.name || code.redeemed_by_user.email}
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  ) : (
                    <span className="text-zinc-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-zinc-400">
                  {code.status === 'redeemed' && code.expires_at ? (
                    <span className="text-green-400">{formatDate(code.expires_at)}</span>
                  ) : (
                    <span className="text-zinc-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-zinc-400">
                  {formatDate(code.created_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  {code.status === 'unused' && (
                    <button 
                      onClick={() => handleRevoke(code.code_id)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Info box */}
      <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <h4 className="text-sm font-medium text-zinc-300 mb-2">How Courtesy Codes Work</h4>
        <ul className="text-sm text-zinc-400 space-y-1">
          <li>• Each code grants 6 months of full access</li>
          <li>• Codes are single-use and non-transferable</li>
          <li>• Redeemed users are labeled &quot;Courtesy Access&quot; in their profile</li>
          <li>• You can revoke unused codes, but not redeemed ones</li>
        </ul>
      </div>
    </div>
  );
}

// ============ FEEDBACK MODULE ============

function FeedbackModule() {
  const [feedback, setFeedback] = useState([]);
  const [counts, setCounts] = useState({ new: 0, reviewed: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [feedbackDetail, setFeedbackDetail] = useState(null);

  useEffect(() => {
    fetchFeedback();
  }, [filter]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = filter ? `${API}/api/feedback/list?status=${filter}` : `${API}/api/feedback/list`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(res.data.feedback || []);
      setCounts(res.data.counts || { new: 0, reviewed: 0, archived: 0 });
    } catch (err) {
      console.error('Failed to load feedback:', err);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbackDetail = async (feedbackId) => {
    try {
      setDetailLoading(true);
      setSelectedFeedback(feedbackId);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackDetail(res.data);
    } catch (err) {
      toast.error('Failed to load feedback details');
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/feedback/${feedbackId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Marked as ${newStatus}`);
      fetchFeedback();
      if (feedbackDetail?.feedback_id === feedbackId) {
        setFeedbackDetail({ ...feedbackDetail, status: newStatus });
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-amber-900/50 text-amber-400',
      reviewed: 'bg-green-900/50 text-green-400',
      archived: 'bg-zinc-700 text-zinc-400'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded ${styles[status] || 'bg-zinc-700 text-zinc-400'}`}>
        {status}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Feedback Submissions</h2>
          <p className="text-sm text-zinc-400 mt-1">
            User feedback from onboarding and settings
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <button 
          onClick={() => setFilter(filter === 'new' ? '' : 'new')}
          className={`bg-zinc-800 rounded-lg p-4 text-left transition-colors ${filter === 'new' ? 'ring-2 ring-amber-500' : ''}`}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            <span className="text-2xl font-bold text-amber-400">{counts.new}</span>
          </div>
          <div className="text-sm text-zinc-400">New</div>
        </button>
        <button 
          onClick={() => setFilter(filter === 'reviewed' ? '' : 'reviewed')}
          className={`bg-zinc-800 rounded-lg p-4 text-left transition-colors ${filter === 'reviewed' ? 'ring-2 ring-green-500' : ''}`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-2xl font-bold text-green-400">{counts.reviewed}</span>
          </div>
          <div className="text-sm text-zinc-400">Reviewed</div>
        </button>
        <button 
          onClick={() => setFilter(filter === 'archived' ? '' : 'archived')}
          className={`bg-zinc-800 rounded-lg p-4 text-left transition-colors ${filter === 'archived' ? 'ring-2 ring-zinc-500' : ''}`}
        >
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-zinc-400" />
            <span className="text-2xl font-bold text-zinc-400">{counts.archived}</span>
          </div>
          <div className="text-sm text-zinc-400">Archived</div>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Feedback List */}
        <div className="bg-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
            <h3 className="font-bold">
              {filter ? `${filter.charAt(0).toUpperCase() + filter.slice(1)} Feedback` : 'All Feedback'}
            </h3>
            <Button variant="outline" size="sm" onClick={fetchFeedback} className="border-zinc-600">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-zinc-400">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : feedback.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No feedback submissions yet</p>
              </div>
            ) : feedback.map((fb) => (
              <div 
                key={fb.feedback_id}
                onClick={() => fetchFeedbackDetail(fb.feedback_id)}
                className={`px-4 py-3 border-b border-zinc-700/50 cursor-pointer hover:bg-zinc-700/30 ${
                  selectedFeedback === fb.feedback_id ? 'bg-zinc-700/50' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm text-zinc-200">
                    {fb.username || 'Anonymous'}
                  </span>
                  {getStatusBadge(fb.status)}
                </div>
                <p className="text-sm text-zinc-400 truncate">{fb.message}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                  <span>{formatDate(fb.created_at)}</span>
                  <span className="bg-zinc-700 px-2 py-0.5 rounded">{fb.source}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="bg-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h3 className="font-bold">Feedback Details</h3>
          </div>
          {detailLoading ? (
            <div className="text-center py-16">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
            </div>
          ) : !feedbackDetail ? (
            <div className="text-center py-16 text-zinc-400">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Select feedback to view details</p>
            </div>
          ) : (
            <div className="p-4">
              {/* User Info */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">From</h4>
                <div className="bg-zinc-900 rounded p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{feedbackDetail.user?.name || feedbackDetail.username || 'Unknown User'}</p>
                      <p className="text-sm text-zinc-400">{feedbackDetail.user?.display_name}</p>
                    </div>
                    <Link 
                      to={feedbackDetail.profile_url}
                      className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1"
                    >
                      View Profile <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Details</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm space-y-1">
                  <p><span className="text-zinc-500">Source:</span> {feedbackDetail.source}</p>
                  <p><span className="text-zinc-500">Submitted:</span> {formatDate(feedbackDetail.created_at)}</p>
                  <p className="flex items-center gap-2">
                    <span className="text-zinc-500">Status:</span> 
                    {getStatusBadge(feedbackDetail.status)}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Message</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {feedbackDetail.message}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {feedbackDetail.status !== 'reviewed' && (
                  <Button 
                    onClick={() => updateStatus(feedbackDetail.feedback_id, 'reviewed')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Reviewed
                  </Button>
                )}
                {feedbackDetail.status !== 'archived' && (
                  <Button 
                    onClick={() => updateStatus(feedbackDetail.feedback_id, 'archived')}
                    variant="outline"
                    className="flex-1 border-zinc-600"
                  >
                    Archive
                  </Button>
                )}
                {feedbackDetail.status === 'archived' && (
                  <Button 
                    onClick={() => updateStatus(feedbackDetail.feedback_id, 'new')}
                    variant="outline"
                    className="flex-1 border-zinc-600"
                  >
                    Restore
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ CODES MODULE ============

function CodesModule() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState({
    code_type: 'beta_invite',
    code: '',
    description: '',
    expires_at: '',
    max_uses: 1
  });

  useEffect(() => {
    fetchCodes();
  }, [filter]);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.type) params.append('code_type', filter.type);
      if (filter.status) params.append('status', filter.status);
      
      const res = await axios.get(`${API}/api/founder/codes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(res.data.codes || []);
    } catch (err) {
      toast.error('Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/founder/codes`, newCode, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code created');
      setShowCreate(false);
      setNewCode({ code_type: 'beta_invite', code: '', description: '', expires_at: '', max_uses: 1 });
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    }
  };

  const handleRevoke = async (codeId) => {
    if (!window.confirm('Revoke this code? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/founder/codes/revoke`, { code_id: codeId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code revoked');
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Codes Management</h2>
        <Button onClick={() => setShowCreate(true)} className="bg-amber-500 hover:bg-amber-600 text-zinc-900">
          <Plus className="w-4 h-4 mr-2" /> Create Code
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <select 
          value={filter.type} 
          onChange={(e) => setFilter({...filter, type: e.target.value})}
          className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm"
        >
          <option value="">All Types</option>
          <option value="beta_invite">Beta Invite</option>
          <option value="unlock">Unlock</option>
        </select>
        <select 
          value={filter.status} 
          onChange={(e) => setFilter({...filter, status: e.target.value})}
          className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="used">Used</option>
          <option value="revoked">Revoked</option>
        </select>
        <Button variant="outline" size="sm" onClick={fetchCodes} className="border-zinc-600">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Create New Code</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Type</label>
                <select 
                  value={newCode.code_type}
                  onChange={(e) => setNewCode({...newCode, code_type: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
                >
                  <option value="beta_invite">Beta Invite</option>
                  <option value="unlock">Unlock</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Code (optional, auto-generate if empty)</label>
                <Input 
                  value={newCode.code}
                  onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                  placeholder="LK-BETA-XXXXXXXX"
                  className="bg-zinc-700 border-zinc-600"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Description</label>
                <Input 
                  value={newCode.description}
                  onChange={(e) => setNewCode({...newCode, description: e.target.value})}
                  placeholder="Internal note about this code"
                  className="bg-zinc-700 border-zinc-600"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Expires At (optional)</label>
                <Input 
                  type="datetime-local"
                  value={newCode.expires_at}
                  onChange={(e) => setNewCode({...newCode, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                  className="bg-zinc-700 border-zinc-600"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Max Uses</label>
                <Input 
                  type="number"
                  min="1"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({...newCode, max_uses: parseInt(e.target.value) || 1})}
                  className="bg-zinc-700 border-zinc-600"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-zinc-600">
                Cancel
              </Button>
              <Button onClick={handleCreate} className="flex-1 bg-amber-500 hover:bg-amber-600 text-zinc-900">
                Create
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Codes Table */}
      <div className="bg-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-700">
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Code</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Type</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Uses</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Created</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-zinc-400">Loading...</td>
              </tr>
            ) : codes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-zinc-400">No codes found</td>
              </tr>
            ) : codes.map((code) => (
              <tr key={code.code_id} className="border-b border-zinc-700/50 hover:bg-zinc-700/30">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono text-amber-400">{code.code}</code>
                    <button onClick={() => copyCode(code.code)} className="text-zinc-500 hover:text-zinc-300">
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded ${
                    code.code_type === 'beta_invite' ? 'bg-blue-900/50 text-blue-400' : 'bg-purple-900/50 text-purple-400'
                  }`}>
                    {code.code_type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded ${
                    code.status === 'active' ? 'bg-green-900/50 text-green-400' : 
                    code.status === 'revoked' ? 'bg-red-900/50 text-red-400' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {code.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-zinc-300">
                  {code.uses_count} / {code.max_uses}
                </td>
                <td className="px-4 py-3 text-sm text-zinc-400">
                  {new Date(code.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right">
                  {code.status === 'active' && code.uses_count === 0 && (
                    <button 
                      onClick={() => handleRevoke(code.code_id)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ MODERATION MODULE ============

function ModerationModule() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'pending', content_type: '' });
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [flagDetail, setFlagDetail] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionForm, setActionForm] = useState({ action: '', reason: '', suspend_days: 7 });
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchQueue();
  }, [filter]);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('status', filter.status);
      if (filter.content_type) params.append('content_type', filter.content_type);
      
      const res = await axios.get(`${API}/api/founder/moderation/queue?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueue(res.data.flags || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error('Failed to load moderation queue');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlagDetail = async (flagId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/founder/moderation/flag/${flagId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlagDetail(res.data);
      setSelectedFlag(flagId);
    } catch (err) {
      toast.error('Failed to load flag details');
    }
  };

  const handleAction = async () => {
    if (!actionForm.action) {
      toast.error('Select an action');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/founder/moderation/action`, {
        flag_id: selectedFlag,
        action: actionForm.action,
        reason: actionForm.reason,
        suspend_days: actionForm.action === 'suspend_user' ? actionForm.suspend_days : undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Action taken successfully');
      setShowActionModal(false);
      setSelectedFlag(null);
      setFlagDetail(null);
      setActionForm({ action: '', reason: '', suspend_days: 7 });
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to take action');
    }
  };

  return (
    <div>
      {/* Stats Bar */}
      <div className="flex gap-4 mb-6">
        <div className="bg-zinc-800 rounded-lg px-4 py-2">
          <span className="text-amber-400 font-bold">{stats.pending || 0}</span>
          <span className="text-zinc-400 text-sm ml-2">Pending</span>
        </div>
        <div className="bg-zinc-800 rounded-lg px-4 py-2">
          <span className="text-green-400 font-bold">{stats.resolved || 0}</span>
          <span className="text-zinc-400 text-sm ml-2">Resolved</span>
        </div>
        <div className="bg-zinc-800 rounded-lg px-4 py-2">
          <span className="text-zinc-400 font-bold">{stats.dismissed || 0}</span>
          <span className="text-zinc-400 text-sm ml-2">Dismissed</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <select 
          value={filter.status} 
          onChange={(e) => setFilter({...filter, status: e.target.value})}
          className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm"
        >
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <select 
          value={filter.content_type} 
          onChange={(e) => setFilter({...filter, content_type: e.target.value})}
          className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm"
        >
          <option value="">All Content Types</option>
          <option value="post">Posts</option>
          <option value="comment">Comments</option>
          <option value="message">Messages</option>
          <option value="server">Servers</option>
          <option value="user">Users</option>
        </select>
        <Button variant="outline" size="sm" onClick={fetchQueue} className="border-zinc-600">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Queue List */}
        <div className="bg-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h3 className="font-bold">Review Queue</h3>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-zinc-400">Loading...</div>
            ) : queue.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">No flags in queue</div>
            ) : queue.map((flag) => (
              <div 
                key={flag.flag_id}
                onClick={() => fetchFlagDetail(flag.flag_id)}
                className={`px-4 py-3 border-b border-zinc-700/50 cursor-pointer hover:bg-zinc-700/30 ${
                  selectedFlag === flag.flag_id ? 'bg-zinc-700/50' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    flag.content_type === 'post' ? 'bg-blue-900/50 text-blue-400' :
                    flag.content_type === 'comment' ? 'bg-purple-900/50 text-purple-400' :
                    flag.content_type === 'message' ? 'bg-cyan-900/50 text-cyan-400' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>
                    {flag.content_type}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {new Date(flag.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-zinc-300 truncate">{flag.reason || 'No reason provided'}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Reported by: {flag.reporter?.name || flag.reporter?.username || 'Unknown'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="bg-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h3 className="font-bold">Flag Details</h3>
          </div>
          {!flagDetail ? (
            <div className="text-center py-16 text-zinc-400">
              <Flag className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Select a flag to view details</p>
            </div>
          ) : (
            <div className="p-4">
              {/* Flag Info */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Flag Information</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm">
                  <p><span className="text-zinc-500">Type:</span> {flagDetail.flag?.content_type}</p>
                  <p><span className="text-zinc-500">Reason:</span> {flagDetail.flag?.reason || 'Not specified'}</p>
                  <p><span className="text-zinc-500">Reported:</span> {new Date(flagDetail.flag?.created_at).toLocaleString()}</p>
                </div>
              </div>

              {/* Reporter */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Reporter</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm">
                  <p>{flagDetail.reporter?.name || flagDetail.reporter?.username || 'Unknown'}</p>
                </div>
              </div>

              {/* Content Owner */}
              {flagDetail.content_owner && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-zinc-400 mb-2">Content Owner</h4>
                  <div className="bg-zinc-900 rounded p-3 text-sm">
                    <p>{flagDetail.content_owner?.name || flagDetail.content_owner?.username}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Flags: {flagDetail.owner_flag_history?.total_flags || 0} | 
                      Warnings: {flagDetail.owner_flag_history?.warnings_count || 0}
                    </p>
                    {flagDetail.content_owner?.is_blocked && (
                      <span className="text-xs bg-red-900/50 text-red-400 px-2 py-0.5 rounded mt-2 inline-block">
                        Blocked
                      </span>
                    )}
                    {flagDetail.content_owner?.suspension_ends_at && (
                      <span className="text-xs bg-amber-900/50 text-amber-400 px-2 py-0.5 rounded mt-2 inline-block">
                        Suspended until {new Date(flagDetail.content_owner.suspension_ends_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Flagged Content */}
              {flagDetail.content && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-zinc-400 mb-2">Flagged Content</h4>
                  <div className="bg-zinc-900 rounded p-3 text-sm max-h-32 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-xs">
                      {JSON.stringify(flagDetail.content, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Actions */}
              {flagDetail.flag?.status === 'pending' && (
                <Button 
                  onClick={() => setShowActionModal(true)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900"
                >
                  Take Action
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Take Moderation Action</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Action</label>
                <select 
                  value={actionForm.action}
                  onChange={(e) => setActionForm({...actionForm, action: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2"
                >
                  <option value="">Select action...</option>
                  <option value="dismiss">Dismiss (No action)</option>
                  <option value="remove_content">Remove Content</option>
                  <option value="warn_user">Warn User</option>
                  <option value="suspend_user">Suspend User</option>
                  <option value="block_user">Block User (Permanent)</option>
                </select>
              </div>
              
              {actionForm.action === 'suspend_user' && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Suspend for (days)</label>
                  <Input 
                    type="number"
                    min="1"
                    max="365"
                    value={actionForm.suspend_days}
                    onChange={(e) => setActionForm({...actionForm, suspend_days: parseInt(e.target.value) || 7})}
                    className="bg-zinc-700 border-zinc-600"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Reason (required for audit)</label>
                <textarea 
                  value={actionForm.reason}
                  onChange={(e) => setActionForm({...actionForm, reason: e.target.value})}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 h-24 resize-none"
                  placeholder="Describe the reason for this action..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => setShowActionModal(false)} 
                className="flex-1 border-zinc-600"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAction}
                disabled={!actionForm.action || !actionForm.reason}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-zinc-900 disabled:opacity-50"
              >
                Confirm Action
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ FINANCIALS MODULE (P1 - Full Implementation) ============

function FinancialsModule({ dashboard }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('all');
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    fetchFinancialSummary();
  }, []);

  const fetchFinancialSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/founder/financials/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(res.data);
    } catch (err) {
      toast.error('Failed to load financial summary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const period = exportPeriod === 'all' ? '' : exportPeriod;
      const includeEmail = window.includeEmailExport || false;
      
      let url = `${API}/api/founder/financials/export?include_email=${includeEmail}`;
      if (period) {
        url += `&period=${period}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert to CSV
      const { columns, data } = res.data;
      const csvContent = [
        columns.join(','),
        ...data.map(row => columns.map(col => {
          const val = row[col];
          // Handle values with commas or quotes
          if (val === null || val === undefined) return '';
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }).join(','))
      ].join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url2 = URL.createObjectURL(blob);
      link.setAttribute('href', url2);
      link.setAttribute('download', `likekind_financials_${res.data.period}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${data.length} records`);
    } catch (err) {
      toast.error('Failed to export data');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const fetchMonthlyData = async (yearMonth) => {
    if (!yearMonth) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/founder/financials/monthly/${yearMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyData(res.data);
    } catch (err) {
      toast.error('Failed to load monthly data');
    }
  };

  // Generate last 12 months for dropdown
  const getLast12Months = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      months.push({ value, label });
    }
    return months;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 text-zinc-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Platform Financials</h2>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchFinancialSummary} 
          className="border-zinc-600"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-zinc-800 rounded-lg p-5">
          <div className="text-2xl font-bold text-green-400">
            ${summary?.revenue?.subscription_mrr?.toFixed(2) || '0.00'}
          </div>
          <div className="text-sm text-zinc-400 mt-1">Monthly Recurring Revenue</div>
          <div className="text-xs text-zinc-500 mt-2">
            {summary?.subscriptions?.total_active || 0} active subscriptions
          </div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-5">
          <div className="text-2xl font-bold text-amber-400">
            ${summary?.earnings?.total_pending?.toFixed(2) || '0.00'}
          </div>
          <div className="text-sm text-zinc-400 mt-1">Pending Payouts</div>
          <div className="text-xs text-zinc-500 mt-2">
            {summary?.payout_threshold?.total_users_with_earnings || 0} users with earnings
          </div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-5">
          <div className="text-2xl font-bold text-blue-400">
            {summary?.payout_threshold?.users_above || 0}
          </div>
          <div className="text-sm text-zinc-400 mt-1">Users ≥ $25</div>
          <div className="text-xs text-zinc-500 mt-2">Payout threshold met</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-5">
          <div className="text-2xl font-bold text-zinc-400">
            {summary?.payout_threshold?.users_below || 0}
          </div>
          <div className="text-sm text-zinc-400 mt-1">Users &lt; $25</div>
          <div className="text-xs text-zinc-500 mt-2">Below threshold</div>
        </div>
      </div>

      {/* Subscription Breakdown */}
      <div className="bg-zinc-800 rounded-lg p-5 mb-6">
        <h3 className="text-lg font-medium mb-4">Subscription Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-700">
                <th className="text-left px-3 py-2 text-zinc-400 font-medium">Plan</th>
                <th className="text-right px-3 py-2 text-zinc-400 font-medium">Count</th>
                <th className="text-right px-3 py-2 text-zinc-400 font-medium">Unit Price</th>
                <th className="text-right px-3 py-2 text-zinc-400 font-medium">MRR</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-zinc-700/50">
                <td className="px-3 py-2">Individual Plus (Monthly)</td>
                <td className="px-3 py-2 text-right">{summary?.subscriptions?.breakdown?.individual_plus_monthly || 0}</td>
                <td className="px-3 py-2 text-right text-zinc-400">$4.99</td>
                <td className="px-3 py-2 text-right text-green-400">
                  ${((summary?.subscriptions?.breakdown?.individual_plus_monthly || 0) * 4.99).toFixed(2)}
                </td>
              </tr>
              <tr className="border-b border-zinc-700/50">
                <td className="px-3 py-2">Individual Plus (Yearly)</td>
                <td className="px-3 py-2 text-right">{summary?.subscriptions?.breakdown?.individual_plus_yearly || 0}</td>
                <td className="px-3 py-2 text-right text-zinc-400">$49/yr</td>
                <td className="px-3 py-2 text-right text-green-400">
                  ${((summary?.subscriptions?.breakdown?.individual_plus_yearly || 0) * (49/12)).toFixed(2)}
                </td>
              </tr>
              <tr className="border-b border-zinc-700/50">
                <td className="px-3 py-2">Business: Local Featured</td>
                <td className="px-3 py-2 text-right">{summary?.subscriptions?.breakdown?.business_local_featured || 0}</td>
                <td className="px-3 py-2 text-right text-zinc-400">$49/mo</td>
                <td className="px-3 py-2 text-right text-green-400">
                  ${((summary?.subscriptions?.breakdown?.business_local_featured || 0) * 49).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2">Business: Market Sponsor</td>
                <td className="px-3 py-2 text-right">{summary?.subscriptions?.breakdown?.business_market_sponsor || 0}</td>
                <td className="px-3 py-2 text-right text-zinc-400">$129/mo</td>
                <td className="px-3 py-2 text-right text-green-400">
                  ${((summary?.subscriptions?.breakdown?.business_market_sponsor || 0) * 129).toFixed(2)}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t border-zinc-600">
                <td className="px-3 py-2 font-bold">Total</td>
                <td className="px-3 py-2 text-right font-bold">{summary?.subscriptions?.total_active || 0}</td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-right font-bold text-green-400">
                  ${summary?.revenue?.subscription_mrr?.toFixed(2) || '0.00'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Monthly View */}
      <div className="bg-zinc-800 rounded-lg p-5 mb-6">
        <h3 className="text-lg font-medium mb-4">Monthly View</h3>
        <div className="flex items-center gap-4 mb-4">
          <select
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(e.target.value);
              fetchMonthlyData(e.target.value);
            }}
            className="bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
          >
            <option value="">Select month...</option>
            {getLast12Months().map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        
        {monthlyData ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-900 rounded p-4">
              <div className="text-xl font-bold text-green-400">+{monthlyData.new_subscriptions}</div>
              <div className="text-xs text-zinc-400">New Subscriptions</div>
            </div>
            <div className="bg-zinc-900 rounded p-4">
              <div className="text-xl font-bold text-red-400">-{monthlyData.canceled_subscriptions}</div>
              <div className="text-xs text-zinc-400">Canceled</div>
            </div>
            <div className="bg-zinc-900 rounded p-4">
              <div className={`text-xl font-bold ${monthlyData.net_subscription_change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {monthlyData.net_subscription_change >= 0 ? '+' : ''}{monthlyData.net_subscription_change}
              </div>
              <div className="text-xs text-zinc-400">Net Change</div>
            </div>
            <div className="bg-zinc-900 rounded p-4">
              <div className="text-xl font-bold text-blue-400">+{monthlyData.new_users}</div>
              <div className="text-xs text-zinc-400">New Users</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-zinc-500">
            Select a month to view data
          </div>
        )}
      </div>

      {/* CSV Export */}
      <div className="bg-zinc-800 rounded-lg p-5">
        <h3 className="text-lg font-medium mb-4">Export Data</h3>
        <p className="text-sm text-zinc-400 mb-4">
          Export financial data as CSV. Columns: user_id, membership_tier, account_status, 
          break_status, earnings_total, earnings_pending, earnings_paid, payout_threshold_met, last_payout_date, created_at.
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          <select
            value={exportPeriod}
            onChange={(e) => setExportPeriod(e.target.value)}
            className="bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
          >
            <option value="all">All Time</option>
            {getLast12Months().map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              id="includeEmail"
              className="w-4 h-4 rounded bg-zinc-700 border-zinc-600"
              onChange={(e) => {
                // Store in component state
                window.includeEmailExport = e.target.checked;
              }}
            />
            Include email (ops only)
          </label>
          <Button 
            onClick={handleExportCSV}
            disabled={exporting}
            className="bg-amber-500 hover:bg-amber-600 text-zinc-900"
          >
            {exporting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </>
            )}
          </Button>
        </div>
        
        <div className="mt-4 p-3 bg-zinc-900 rounded text-xs text-zinc-500">
          <strong>Note:</strong> Payout functionality is not yet enabled. Earnings are tracked but 
          payouts require Stripe Connect integration (future release).
        </div>
      </div>

      {/* Generated timestamp */}
      {summary?.generated_at && (
        <div className="mt-4 text-xs text-zinc-500 text-right">
          Last updated: {new Date(summary.generated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ============ USERS MODULE (P2 - Full Implementation) ============

function UsersModule() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState({ type: 'user_id', value: '' });
  const [filters, setFilters] = useState({ account_status: '', moderation_status: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const handleSearch = async () => {
    if (!search.value && !filters.account_status && !filters.moderation_status) {
      toast.error('Enter a search term or select a filter');
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (search.value) {
        params.append(search.type, search.value);
      }
      if (filters.account_status) {
        params.append('account_status', filters.account_status);
      }
      if (filters.moderation_status) {
        params.append('moderation_status', filters.moderation_status);
      }
      
      const res = await axios.get(`${API}/api/founder/users/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Search failed');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      setDetailLoading(true);
      setSelectedUser(userId);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/founder/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetail(res.data);
    } catch (err) {
      toast.error('Failed to load user details');
    } finally {
      setDetailLoading(false);
    }
  };

  const getModerationBadge = (status) => {
    const badges = {
      clean: { bg: 'bg-green-900/50', text: 'text-green-400', label: 'Clean' },
      warned: { bg: 'bg-amber-900/50', text: 'text-amber-400', label: 'Warned' },
      suspended: { bg: 'bg-orange-900/50', text: 'text-orange-400', label: 'Suspended' },
      blocked: { bg: 'bg-red-900/50', text: 'text-red-400', label: 'Blocked' }
    };
    const badge = badges[status] || badges.clean;
    return <span className={`text-xs px-2 py-0.5 rounded ${badge.bg} ${badge.text}`}>{badge.label}</span>;
  };

  const getAccountBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-900/50', text: 'text-green-400', label: 'Active' },
      on_break: { bg: 'bg-blue-900/50', text: 'text-blue-400', label: 'On Break' },
      deleted: { bg: 'bg-zinc-700', text: 'text-zinc-400', label: 'Deleted' }
    };
    const badge = badges[status] || badges.active;
    return <span className={`text-xs px-2 py-0.5 rounded ${badge.bg} ${badge.text}`}>{badge.label}</span>;
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-6">User Lookup</h2>
      
      {/* Search Bar */}
      <div className="bg-zinc-800 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-zinc-400 mb-1">Search by</label>
            <div className="flex gap-2">
              <select
                value={search.type}
                onChange={(e) => setSearch({...search, type: e.target.value})}
                className="bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
              >
                <option value="user_id">User ID</option>
                <option value="email">Email</option>
                <option value="username">Username</option>
              </select>
              <Input
                value={search.value}
                onChange={(e) => setSearch({...search, value: e.target.value})}
                placeholder={`Enter ${search.type}...`}
                className="bg-zinc-700 border-zinc-600 flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Account Status</label>
            <select
              value={filters.account_status}
              onChange={(e) => setFilters({...filters, account_status: e.target.value})}
              className="bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
            >
              <option value="">Any</option>
              <option value="active">Active</option>
              <option value="on_break">On Break</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Moderation</label>
            <select
              value={filters.moderation_status}
              onChange={(e) => setFilters({...filters, moderation_status: e.target.value})}
              className="bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-sm"
            >
              <option value="">Any</option>
              <option value="warned">Warned</option>
              <option value="suspended">Suspended</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <Button onClick={handleSearch} className="bg-amber-500 hover:bg-amber-600 text-zinc-900">
            Search
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* User List */}
        <div className="bg-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
            <h3 className="font-bold">Results</h3>
            {total > 0 && <span className="text-xs text-zinc-400">{total} found</span>}
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-zinc-400">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Search for users to view their status</p>
              </div>
            ) : users.map((u) => (
              <div 
                key={u.user_id}
                onClick={() => fetchUserDetail(u.user_id)}
                className={`px-4 py-3 border-b border-zinc-700/50 cursor-pointer hover:bg-zinc-700/30 ${
                  selectedUser === u.user_id ? 'bg-zinc-700/50' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{u.name || u.username || 'No name'}</span>
                  <div className="flex gap-1">
                    {getAccountBadge(u.account_status)}
                    {getModerationBadge(u.moderation?.status)}
                  </div>
                </div>
                <p className="text-xs text-zinc-400 truncate">ID: {u.user_id}</p>
                <div className="flex gap-4 mt-1 text-xs text-zinc-500">
                  <span>Tier: {u.membership?.tier || 'free'}</span>
                  <span>Earnings: ${u.financials?.earnings?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="bg-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h3 className="font-bold">User Details</h3>
          </div>
          {detailLoading ? (
            <div className="text-center py-16">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
            </div>
          ) : !userDetail ? (
            <div className="text-center py-16 text-zinc-400">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Select a user to view details</p>
            </div>
          ) : (
            <div className="p-4 max-h-[500px] overflow-y-auto">
              {/* Basic Info */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Basic Info</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm space-y-1">
                  <p><span className="text-zinc-500">Name:</span> {userDetail.user?.name || '—'}</p>
                  <p><span className="text-zinc-500">Username:</span> {userDetail.user?.username || '—'}</p>
                  <p><span className="text-zinc-500">Email:</span> {userDetail.user?.email || '—'}</p>
                  <p><span className="text-zinc-500">User ID:</span> <code className="text-xs">{userDetail.user?.user_id}</code></p>
                  <p><span className="text-zinc-500">Type:</span> {userDetail.user?.is_business ? 'Business' : 'Individual'}</p>
                  <p><span className="text-zinc-500">Joined:</span> {userDetail.user?.created_at ? new Date(userDetail.user.created_at).toLocaleDateString() : '—'}</p>
                </div>
              </div>

              {/* Account Status */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Account Status</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm space-y-1">
                  <p className="flex items-center gap-2">
                    <span className="text-zinc-500">Status:</span> 
                    {getAccountBadge(userDetail.account?.status)}
                  </p>
                  {userDetail.account?.status === 'on_break' && (
                    <>
                      <p><span className="text-zinc-500">Break Start:</span> {userDetail.account?.break_start ? new Date(userDetail.account.break_start).toLocaleDateString() : '—'}</p>
                      <p><span className="text-zinc-500">Break Ends:</span> {userDetail.account?.break_ends ? new Date(userDetail.account.break_ends).toLocaleDateString() : '—'}</p>
                    </>
                  )}
                  <p><span className="text-zinc-500">Last Active:</span> {userDetail.account?.last_active ? new Date(userDetail.account.last_active).toLocaleString() : 'Unknown'}</p>
                </div>
              </div>

              {/* Membership */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Membership</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm space-y-1">
                  <p><span className="text-zinc-500">Tier:</span> <span className="text-amber-400">{userDetail.membership?.tier || 'free'}</span></p>
                  <p><span className="text-zinc-500">Subscription:</span> {userDetail.membership?.subscription?.status || 'None'}</p>
                  {userDetail.membership?.subscription?.current_period_end && (
                    <p><span className="text-zinc-500">Renews:</span> {new Date(userDetail.membership.subscription.current_period_end).toLocaleDateString()}</p>
                  )}
                </div>
              </div>

              {/* Financials */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Financials</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm space-y-1">
                  <p><span className="text-zinc-500">Earnings:</span> <span className="text-green-400">${userDetail.financials?.earnings?.toFixed(2) || '0.00'}</span></p>
                  <p><span className="text-zinc-500">Payout Threshold:</span> {userDetail.financials?.payout_threshold_met ? 
                    <span className="text-green-400">✓ Met ($25+)</span> : 
                    <span className="text-zinc-400">Not met</span>
                  }</p>
                </div>
              </div>

              {/* Moderation */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-zinc-400 mb-2">Moderation</h4>
                <div className="bg-zinc-900 rounded p-3 text-sm space-y-1">
                  <p className="flex items-center gap-2">
                    <span className="text-zinc-500">Status:</span>
                    {getModerationBadge(userDetail.moderation?.status)}
                  </p>
                  <p><span className="text-zinc-500">Warnings:</span> {userDetail.moderation?.warnings_count || 0}</p>
                  <p><span className="text-zinc-500">Flags Received:</span> {userDetail.moderation?.flags_history?.length || 0}</p>
                  {userDetail.moderation?.is_blocked && (
                    <p><span className="text-zinc-500">Block Reason:</span> {userDetail.moderation?.block_reason || '—'}</p>
                  )}
                  {userDetail.moderation?.is_suspended && (
                    <>
                      <p><span className="text-zinc-500">Suspended Until:</span> {userDetail.moderation?.suspension_ends_at ? new Date(userDetail.moderation.suspension_ends_at).toLocaleDateString() : '—'}</p>
                      <p><span className="text-zinc-500">Suspension Reason:</span> {userDetail.moderation?.suspension_reason || '—'}</p>
                    </>
                  )}
                </div>
              </div>

              {/* Flags History */}
              {userDetail.moderation?.flags_history?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-zinc-400 mb-2">Recent Flags</h4>
                  <div className="bg-zinc-900 rounded p-3 text-xs space-y-2 max-h-32 overflow-y-auto">
                    {userDetail.moderation.flags_history.map((flag, i) => (
                      <div key={i} className="border-b border-zinc-700/50 pb-2 last:border-0 last:pb-0">
                        <div className="flex justify-between">
                          <span className="text-zinc-400">{flag.content_type}</span>
                          <span className={flag.status === 'pending' ? 'text-amber-400' : 'text-zinc-500'}>{flag.status}</span>
                        </div>
                        <p className="text-zinc-500 truncate">{flag.reason || 'No reason'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 p-2 bg-zinc-900 rounded text-xs text-zinc-500 text-center">
                Read-only view • No admin actions available
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ APPEARANCE MODULE ============
// Founder-only controls for category page backgrounds

// ============================================================================
// SELF-HOSTED SEASONAL IMAGES (no external dependencies)
// Backgrounds appear BEHIND category pages, never on buttons
// Non-targeted, feed-neutral
// ============================================================================
const SEASONAL_IMAGES = [
  { id: 'default', label: 'Warm Earth (Default)', url: '/api/static/seed/backgrounds/bg_default_earth.jpg', season: 'all' },
  { id: 'spring', label: 'Spring Green', url: '/api/static/seed/backgrounds/bg_spring_green.jpg', season: 'spring' },
  { id: 'summer', label: 'Summer Warm', url: '/api/static/seed/backgrounds/bg_summer_warm.jpg', season: 'summer' },
  { id: 'autumn', label: 'Autumn Amber', url: '/api/static/seed/backgrounds/bg_autumn_amber.jpg', season: 'autumn' },
  { id: 'winter', label: 'Winter White', url: '/api/static/seed/backgrounds/bg_winter_white.jpg', season: 'winter' },
  { id: 'creative', label: 'Creative Blue', url: '/api/static/seed/backgrounds/bg_creative_blue.jpg', season: 'all' },
];

const EARTH_TONE_COLORS = [
  { id: 'sage', label: 'Sage', value: 'var(--lk-sage)' },
  { id: 'terra', label: 'Terracotta', value: 'var(--lk-terra)' },
  { id: 'sand', label: 'Sand', value: 'var(--lk-sand)' },
  { id: 'moss', label: 'Moss', value: 'var(--lk-moss)' },
  { id: 'stone', label: 'Stone', value: 'var(--lk-stone)' },
  { id: 'warm', label: 'Warm', value: 'var(--lk-warm)' },
];

function AppearanceModule() {
  const [globalSettings, setGlobalSettings] = useState({
    mode: 'seasonal',
    seasonal_image: 'default',
    solid_color: 'sage',
    custom_image_url: '',
    auto_rotate: true
  });
  const [perCategorySettings, setPerCategorySettings] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // null = global settings
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/categories`);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/founder/appearance-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        if (res.data.global) {
          setGlobalSettings(prev => ({ ...prev, ...res.data.global }));
        }
        if (res.data.per_category) {
          setPerCategorySettings(res.data.per_category);
        }
      }
    } catch (error) {
      // Settings might not exist yet - use defaults
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/founder/appearance-settings`, {
        global: globalSettings,
        per_category: perCategorySettings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appearance settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCustomUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'category_background');
      
      const uploadRes = await axios.post(`${API}/api/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (selectedCategory) {
        setPerCategorySettings(prev => ({
          ...prev,
          [selectedCategory]: {
            ...prev[selectedCategory],
            custom_image_url: uploadRes.data.url
          }
        }));
      } else {
        setGlobalSettings(prev => ({ ...prev, custom_image_url: uploadRes.data.url }));
      }
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const getCurrentSettings = () => {
    if (selectedCategory && perCategorySettings[selectedCategory]) {
      return { ...globalSettings, ...perCategorySettings[selectedCategory] };
    }
    return globalSettings;
  };

  const updateCurrentSettings = (updates) => {
    if (selectedCategory) {
      setPerCategorySettings(prev => ({
        ...prev,
        [selectedCategory]: {
          ...prev[selectedCategory],
          ...updates
        }
      }));
    } else {
      setGlobalSettings(prev => ({ ...prev, ...updates }));
    }
  };

  const clearCategoryOverride = (categoryId) => {
    setPerCategorySettings(prev => {
      const newSettings = { ...prev };
      delete newSettings[categoryId];
      return newSettings;
    });
    toast.success('Category override removed');
  };

  const getCurrentPreview = () => {
    const settings = getCurrentSettings();
    if (settings.mode === 'seasonal') {
      const img = SEASONAL_IMAGES.find(i => i.id === settings.seasonal_image);
      return img?.url || SEASONAL_IMAGES[0].url;
    }
    if (settings.mode === 'custom') {
      return settings.custom_image_url || '';
    }
    return '';
  };

  const settings = getCurrentSettings();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">Category Atmosphere Controls</h2>
        <p className="text-zinc-400 text-sm">
          Manage background imagery for category and subcategory pages. Backgrounds appear BEHIND content, never on buttons. Non-targeted, feed-neutral.
        </p>
      </div>

      {/* Category Selector */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="font-semibold text-white mb-4">Scope</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === null 
                ? 'bg-amber-500 text-black' 
                : 'bg-zinc-700 text-white hover:bg-zinc-600'
            }`}
          >
            🌍 Global (All Categories)
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.id 
                  ? 'bg-amber-500 text-black' 
                  : perCategorySettings[cat.id] 
                    ? 'bg-green-600 text-white hover:bg-green-500' 
                    : 'bg-zinc-700 text-white hover:bg-zinc-600'
              }`}
            >
              {cat.name} {perCategorySettings[cat.id] && '✓'}
            </button>
          ))}
        </div>
        {selectedCategory && (
          <div className="mt-4 p-3 bg-zinc-700 rounded-lg flex items-center justify-between">
            <p className="text-sm text-zinc-300">
              Editing: <span className="font-semibold text-white">{categories.find(c => c.id === selectedCategory)?.name}</span>
              {perCategorySettings[selectedCategory] ? ' (has custom override)' : ' (using global)'}
            </p>
            {perCategorySettings[selectedCategory] && (
              <button
                onClick={() => clearCategoryOverride(selectedCategory)}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Clear Override
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mode Selector — "custom" mode disabled for beta pending a stable
          upload endpoint. Existing custom_image_url values (if any) still
          render on member surfaces; new uploads are not offered. */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <h3 className="font-semibold text-white mb-4">Background Mode</h3>
        <div className="grid grid-cols-2 gap-4">
          {['seasonal', 'color'].map(mode => (
            <button
              key={mode}
              onClick={() => updateCurrentSettings({ mode })}
              className={`p-4 rounded-lg border-2 transition-all ${
                settings.mode === mode 
                  ? 'border-amber-500 bg-amber-500/10' 
                  : 'border-zinc-700 hover:border-zinc-600'
              }`}
            >
              <p className="font-medium text-white capitalize">
                {mode === 'color' ? 'Solid Color' : 'Seasonal Image'}
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                {mode === 'seasonal' && 'Self-hosted library'}
                {mode === 'color' && 'Earth tone solid'}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Seasonal Image Library */}
      {settings.mode === 'seasonal' && (
        <div className="bg-zinc-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">Seasonal Image Library (Self-Hosted)</h3>
            {!selectedCategory && (
              <label className="flex items-center gap-2 text-sm text-zinc-400">
                <input
                  type="checkbox"
                  checked={settings.auto_rotate}
                  onChange={(e) => updateCurrentSettings({ auto_rotate: e.target.checked })}
                  className="rounded"
                />
                Auto-rotate by season
              </label>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SEASONAL_IMAGES.map(img => (
              <button
                key={img.id}
                onClick={() => updateCurrentSettings({ seasonal_image: img.id })}
                className={`relative rounded-lg overflow-hidden aspect-video border-2 transition-all ${
                  settings.seasonal_image === img.id 
                    ? 'border-amber-500 ring-2 ring-amber-500/50' 
                    : 'border-transparent hover:border-zinc-600'
                }`}
              >
                <img 
                  src={`${API}${img.url}`}
                  alt={img.label}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="text-white text-sm font-medium">{img.label}</p>
                  <p className="text-white/60 text-xs">{img.season === 'all' ? 'All seasons' : img.season}</p>
                </div>
                {settings.seasonal_image === img.id && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Solid Color Palette */}
      {settings.mode === 'color' && (
        <div className="bg-zinc-800 rounded-lg p-6">
          <h3 className="font-semibold text-white mb-4">Earth Tone Palette</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {EARTH_TONE_COLORS.map(color => (
              <button
                key={color.id}
                onClick={() => updateCurrentSettings({ solid_color: color.id })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.solid_color === color.id 
                    ? 'border-amber-500' 
                    : 'border-zinc-700 hover:border-zinc-600'
                }`}
              >
                <div 
                  className="w-full aspect-square rounded-lg mb-2"
                  style={{ backgroundColor: color.value }}
                />
                <p className="text-xs text-white text-center">{color.label}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Upload block removed for beta — endpoint not yet available.
          Preserves any previously saved custom_image_url on the settings
          record so member surfaces still render existing selections. */}

      {/* Live Preview */}
      <div className="bg-zinc-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">Preview</h3>
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className={`text-sm px-3 py-1 rounded-full ${previewMode ? 'bg-amber-500 text-black' : 'bg-zinc-700 text-white'}`}
          >
            {previewMode ? 'Exit Preview' : 'Full Preview'}
          </button>
        </div>
        <div 
          className={`rounded-lg flex items-center justify-center relative overflow-hidden transition-all ${previewMode ? 'aspect-[16/9]' : 'aspect-video'}`}
          style={{
            backgroundColor: settings.mode === 'color' 
              ? EARTH_TONE_COLORS.find(c => c.id === settings.solid_color)?.value || 'var(--lk-sage)'
              : '#1f1f1f'
          }}
        >
          {(settings.mode === 'seasonal' || settings.mode === 'custom') && getCurrentPreview() && (
            <img 
              src={getCurrentPreview().startsWith('/') ? `${API}${getCurrentPreview()}` : getCurrentPreview()}
              alt="Preview"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: 0.15 }}
            />
          )}
          <div className="relative z-10 text-center p-8">
            <p className="text-white/40 text-xs mb-4 uppercase tracking-wider">
              Background appears behind content (opacity: 15%)
            </p>
            <p className="text-white/80 text-lg font-semibold mb-4">
              {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : 'All Categories'}
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
              {['Outdoors', 'Music', 'Food'].map(cat => (
                <div key={cat} className="bg-white/95 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 shadow-lg">
                  {cat}
                </div>
              ))}
            </div>
            <p className="text-white/40 text-xs mt-4">
              ↑ Category buttons remain solid (no overlay)
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          onClick={fetchSettings}
          variant="outline"
          className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
        >
          Reset
        </Button>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-600 text-black font-medium px-8"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}


// ============ FOUNDER CONSTITUTION MODULE ============
// READ-ONLY - Displays the FULL BETA BUILD DIRECTIVE verbatim
// No edit, no versioning, no controls
// This is a constitutional reference, not a feature

function ConstitutionModule() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <ScrollText className="w-6 h-6 text-amber-500" />
        <div>
          <h2 className="text-xl font-bold text-white">Founder Constitution</h2>
          <p className="text-sm text-zinc-400">Read-only reference • Full Beta Build Directive</p>
        </div>
      </div>

      <div className="bg-zinc-800 rounded-lg p-6 md:p-8 overflow-auto max-h-[70vh] font-mono text-sm leading-relaxed">
        <pre className="text-zinc-300 whitespace-pre-wrap">
{`LIKEKIND — FULL BETA BUILD DIRECTIVE (LOCKED)
STATUS: FULL BETA — EXECUTION MODE
AUTHORITY: Founder-approved. Philosophy locked.
SCOPE: All aligned systems active.
SUPERSEDES: All prior interpretations.

This is not exploratory.
Do not redesign.
Do not expand scope.
If ambiguity exists → STOP and escalate.

────────────────────────────────────────
PRIORITY ORDER (NON-NEGOTIABLE)
────────────────────────────────────────
1. Platform foundation & structural correctness (zero errors)
2. User privacy (architectural, not policy-based)
3. User experience, engagement, and fun
4. Business & sponsor participation (contained)
5. Founder preference (last)

If a decision conflicts, resolve it in this order.

────────────────────────────────────────
FOUNDER CLARIFICATION ADDENDUM (LOCKED)
────────────────────────────────────────
• Static placards MAY appear on:
  - User profiles
  - Category pages
  - Place pages
  Provided they are:
  - Static (no performance rotation)
  - Non-targeted
  - Non-optimized
  - Do not influence feeds, ranking, or visibility

• "One app" refers to the USER EXPERIENCE and mental model.
  - Companion apps or background services may exist
  - Users must never feel they entered a different mode, tier, or paid area

• If this directive conflicts with any previously locked LikeKind build spec:
  → Escalate to founder.
  → Do NOT default to removal or reinterpretation.

────────────────────────────────────────
CORE CANONICAL MODEL (LOCKED)
────────────────────────────────────────
Canonical Objects:
• Profile = a person
  - Individuals and independent businesses share the same base profile
• Place = where interaction happens
• Commons = continuity guarantee for a Place

Critical Distinctions:
• Public vs Private = visibility only
• Commons ≠ privacy
• Commons ≠ access
• Commons ≠ features
• Commons = paying to keep a place steady over time

────────────────────────────────────────
WHAT MUST NOT CHANGE
────────────────────────────────────────
• Connection feed behavior
• Circles
• Meetups
• Category + location discovery
• Chronological / finite feed logic
• Social feel and "fun" of the app

THE FEED IS SACRED:
• Never monetized
• Never ranked by money
• Never targeted
• Never influenced by sponsors or business logic

If sponsor logic touches the feed in any way → disable it.

────────────────────────────────────────
USER-FACING STRUCTURE (NO MODES, NO SWITCHES)
────────────────────────────────────────
• One app
• One experience
• Everything starts as people connecting
• Some places naturally persist

Users should NEVER feel like they entered:
• a "paid area"
• a "business mode"
• a "sponsored experience"

────────────────────────────────────────
PLACES (CORE INTERACTION UNIT)
────────────────────────────────────────
A Place can be:
• Public or private (creator choice)
• Lightweight and temporary by default

A Place may become Commons-backed only when a steward opts in.

Commons Activation (UX):
Language only — minimal logic:
"This place has been active for a while.
Keeping it means it stays searchable, steady, and stewarded.
Nothing else changes."

Button:
• "Keep this place"

Commons applies equally to public or private places.

Commons ENABLES (ONLY):
• Archives & persistence
• Steward tools
• Governance / moderation controls
• Continuity guarantees
• Succession support

Commons DOES NOT:
• Change visibility
• Gate participation
• Add features to non-stewards
• Affect feeds or discovery

────────────────────────────────────────
MEMBERSHIP (INDIVIDUALS)
────────────────────────────────────────
Membership = commitment signal only.

Membership does NOT:
• Boost visibility
• Change feed placement
• Affect ranking
• Grant power over others

User-facing copy:
"Membership shows you're here on purpose.
It doesn't make you louder."

────────────────────────────────────────
INDEPENDENT BUSINESS / SOLO OPERATOR PROFILES
────────────────────────────────────────
• Independent businesses are people first
• Same base profile as individuals
• One additional marker only (choose one, be consistent):
  - "Independent"
  - "Practicing"
  - "Studio"

They get:
• A calm, durable place for their work
• Legibility and continuity
• Ability to host meetups and updates

They do NOT get:
• Boosts
• CTAs
• Funnels
• Analytics dashboards
• Lead-gen tools
• On-platform payments
• Feed advantage

All payments are off-platform.

────────────────────────────────────────
BUSINESS MEMBERSHIPS
────────────────────────────────────────
Businesses may steward Places.
They pay for:
• Presence
• Continuity
• Responsibility

They do NOT buy:
• Reach
• Targeting
• Amplification
• Feed influence

────────────────────────────────────────
SPONSORSHIP / ADVERTISING (STRICTLY LIMITED)
────────────────────────────────────────
ALLOWED (ONLY THIS):
• Static placards
• Place-bound or profile-bound acknowledgements
• Fixed duration
• Same for all users
• Clearly disclosed
• Language: "Supported by"

ABSOLUTELY FORBIDDEN:
• Sponsored posts
• Feed insertion
• Targeting
• Rotation by performance
• Impressions / clicks optimization
• Auctions
• Behavioral data usage

────────────────────────────────────────
SUPPORTED VS SPONSOR-FREE PRICING (BETA-READY)
────────────────────────────────────────
• Same features
• Same experience
• Difference is values choice, not advantage

────────────────────────────────────────
PRIVACY (ARCHITECTURAL ENFORCEMENT)
────────────────────────────────────────
• No targeting systems exist
• No behavioral tracking for monetization
• No data collected "just in case"
• No analytics that shape attention

If privacy relies on "we won't use this" → FAIL
If privacy relies on "we can't use this" → PASS

────────────────────────────────────────
FEED RULES (PERMANENT)
────────────────────────────────────────
• Chronological
• Finite
• Legible
• Relationship-based
• No global ranking
• No trending
• No paid influence

────────────────────────────────────────
EXPLICIT DEFERRALS (DO NOT BUILD)
────────────────────────────────────────
• Email system fixes
• Stripe / Mercury wiring
• Wallets
• Peer-to-peer payments
• Stored value
• Financial rakes

────────────────────────────────────────
BETA FREEZE RULES
────────────────────────────────────────
During beta:
• Bug fixes allowed
• Copy clarity allowed
• Stability improvements allowed
• NO new systems
• NO new monetization mechanics
• NO philosophy changes

────────────────────────────────────────
DEFINITION OF DONE
────────────────────────────────────────
Emergent is done when:
• The app behaves the same
• The app reads clearer
• No system contradicts:
  - Privacy
  - Continuity
  - Engagement without manipulation
• No monetization touches attention

────────────────────────────────────────
CORE SENTENCE (INTERNAL ALIGNMENT)
────────────────────────────────────────
LikeKind organizes people for free.
Places pay only when they're worth keeping.
Sponsors may support places — never shape attention.`}
        </pre>
      </div>

      <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
        <p className="text-xs text-zinc-500 text-center">
          This document is read-only and serves as the constitutional reference for LikeKind development.
          <br />
          Manual updates only. No edit UI. No versioning UI.
        </p>
      </div>
    </div>
  );
}



// ============ BUSINESS VERIFICATION MODULE ============

function BusinessVerificationModule() {
  const [queue, setQueue] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reviewNotes, setReviewNotes] = useState({});
  const [processing, setProcessing] = useState(null);

  const fetchQueue = async (statusFilter) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/admin/business/verification/queue?status_filter=${statusFilter || filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueue(res.data.businesses || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      console.error('Verification queue error:', err);
      toast.error('Failed to load verification queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQueue(filter); }, [filter]);

  const handleReview = async (businessId, decision) => {
    try {
      setProcessing(businessId);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/business/${businessId}/verification/review`, {
        decision,
        notes: reviewNotes[businessId] || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Business ${decision === 'verified' ? 'approved' : 'rejected'}`);
      setReviewNotes(prev => ({ ...prev, [businessId]: '' }));
      fetchQueue(filter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review failed');
    } finally {
      setProcessing(null);
    }
  };

  const statusColor = (s) => {
    if (s === 'verified') return 'text-emerald-400';
    if (s === 'pending') return 'text-amber-400';
    if (s === 'rejected') return 'text-red-400';
    return 'text-zinc-500';
  };

  const statusIcon = (s) => {
    if (s === 'verified') return <CheckCircle className="w-4 h-4" />;
    if (s === 'pending') return <Clock className="w-4 h-4" />;
    if (s === 'rejected') return <XCircle className="w-4 h-4" />;
    return null;
  };

  return (
    <div data-testid="verification-module">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white">Business Verification</h2>
        <Button variant="ghost" size="sm" onClick={() => fetchQueue(filter)} className="text-zinc-400 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-zinc-800 rounded-lg p-3 text-center cursor-pointer hover:ring-1 hover:ring-zinc-600" onClick={() => setFilter('all')}>
          <div className="text-xl font-bold text-zinc-100">{counts.all || 0}</div>
          <div className="text-xs text-zinc-500">All</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-3 text-center cursor-pointer hover:ring-1 hover:ring-amber-600" onClick={() => setFilter('pending')}>
          <div className="text-xl font-bold text-amber-400">{counts.pending || 0}</div>
          <div className="text-xs text-zinc-500">Pending</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-3 text-center cursor-pointer hover:ring-1 hover:ring-emerald-600" onClick={() => setFilter('verified')}>
          <div className="text-xl font-bold text-emerald-400">{counts.verified || 0}</div>
          <div className="text-xs text-zinc-500">Verified</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-3 text-center cursor-pointer hover:ring-1 hover:ring-zinc-600" onClick={() => setFilter('unverified')}>
          <div className="text-xl font-bold text-zinc-400">{counts.unverified || 0}</div>
          <div className="text-xs text-zinc-500">Unverified</div>
        </div>
      </div>

      {/* Filter indicator */}
      <div className="flex items-center gap-2 mb-4 text-sm text-zinc-400">
        <span>Showing:</span>
        <span className={`font-medium capitalize ${filter === 'pending' ? 'text-amber-400' : filter === 'verified' ? 'text-emerald-400' : 'text-zinc-300'}`}>{filter}</span>
        <span>({queue.length})</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : queue.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No businesses in this queue</p>
        </div>
      ) : (
        <div className="space-y-3">
          {queue.map(biz => (
            <div key={biz.user_id} className="bg-zinc-800 rounded-lg border border-zinc-700 p-4" data-testid={`verification-card-${biz.user_id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white truncate">{biz.business_name || 'Unnamed'}</h3>
                    <span className={`flex items-center gap-1 text-xs ${statusColor(biz.verification_status)}`}>
                      {statusIcon(biz.verification_status)}
                      {biz.verification_status}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 space-y-0.5">
                    <div>{biz.business_category} &middot; {biz.business_city}, {biz.business_state}</div>
                    {biz.verification_contact_email && <div>Contact: {biz.verification_contact_email}</div>}
                    {biz.verification_website && (
                      <div>
                        Web: <a href={biz.verification_website.startsWith('http') ? biz.verification_website : `https://${biz.verification_website}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{biz.verification_website}</a>
                      </div>
                    )}
                    {biz.verification_description && <div className="text-zinc-400 mt-1">{biz.verification_description}</div>}
                    {biz.verification_submitted_at && <div>Submitted: {new Date(biz.verification_submitted_at).toLocaleDateString()}</div>}
                    {biz.verification_reviewed_at && <div>Reviewed: {new Date(biz.verification_reviewed_at).toLocaleDateString()}</div>}
                    {biz.verification_notes && <div className="text-amber-400/80">Note: {biz.verification_notes}</div>}
                  </div>
                </div>
              </div>

              {/* Review controls — show for pending, or allow re-review */}
              {(biz.verification_status === 'pending' || biz.verification_status === 'rejected' || biz.verification_status === 'unverified') && (
                <div className="border-t border-zinc-700 pt-3 mt-3">
                  <Input
                    placeholder="Review notes (optional)"
                    value={reviewNotes[biz.user_id] || ''}
                    onChange={(e) => setReviewNotes(prev => ({ ...prev, [biz.user_id]: e.target.value }))}
                    className="bg-zinc-900 border-zinc-600 text-zinc-200 text-sm mb-2"
                    data-testid={`review-notes-${biz.user_id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleReview(biz.user_id, 'verified')}
                      disabled={processing === biz.user_id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                      data-testid={`approve-btn-${biz.user_id}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleReview(biz.user_id, 'rejected')}
                      disabled={processing === biz.user_id}
                      className="text-red-400 hover:text-red-300 hover:bg-red-950/30 text-xs"
                      data-testid={`reject-btn-${biz.user_id}`}
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
